/**
 * Decides whether Ryn Replay capture should be paused because keyboard focus has
 * entered a heavy element (#31398: focusing an editable cell in the dense
 * Equipment Usage Kendo grid starts the per-keystroke DOM mutation storm the
 * recorder was serializing on the browser main thread, which froze the grid for
 * everyone keying equipment).
 *
 * Pure on purpose, so the pause decision is unit-tested without a live recorder.
 * The service wires this to document focusin/focusout and toggles the recorder's
 * existing pause()/resume(): they detach and re-attach the rrweb listeners, and
 * resume() re-emits a fresh FullSnapshot from a known-complete DOM, so the
 * replay keeps the whole screen before, a short gap only while actively typing,
 * and the grid's final values after.
 *
 * "Heavy" is a configured list of CSS selectors (e.g. the Equipment Usage page
 * host `ups-equipment-usage-page`). A focus target counts as heavy when it, or
 * any ancestor, matches one of them (`Element.closest`). Focus moving between
 * two elements that are both heavy (cell to cell) stays paused; capture only
 * resumes when focus has left every heavy element, which is what stops resume()
 * from emitting a FullSnapshot on every keystroke.
 */
export function isFocusWithinHeavyElement(
  target: EventTarget | null | undefined,
  heavySelectors: readonly string[] | undefined
): boolean {
  if (!target || !heavySelectors || heavySelectors.length === 0) {
    return false;
  }

  const element = target as Element;
  if (typeof element.closest !== 'function') {
    // Not an Element (Document, Window, or a bare EventTarget): it cannot sit
    // inside a heavy element, and calling closest on it would throw.
    return false;
  }

  return heavySelectors.some(
    (selector) => selector.length > 0 && element.closest(selector) != null
  );
}
